import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import Layout from "../layouts/Layout";
import { firestore, doc, getDoc } from '../firebase-config';
import DOMPurify from 'dompurify';
import "../styles/BlogPost.css";
import arrow_icon from '../assets/icons/arrow-left.svg';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return timestamp.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const postDocRef = doc(firestore, 'posts', id);
        const postSnap = await getDoc(postDocRef);
        if (postSnap.exists()) {
          setPost({ id: postSnap.id, ...postSnap.data() });
        } else {
          setError('Post not found.');
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        setError('Failed to load post.');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  useEffect(() => {
  // window.scrollTo(0, 110);
  window.scrollTo(0, 0);
}, [id]);

  // Close lightbox on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setLightboxImg(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  if (loading) {
    return (
      <Layout featureType="blog">
        <Helmet>
          <title>Blog – Crich Baptist Church</title>
        </Helmet>
        <h2>Loading...</h2>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout featureType="blog">
        <Helmet>
          <title>Post Not Found – Crich Baptist Church</title>
        </Helmet>
        <h2>Error</h2>
        <p>{error}</p>
      </Layout>
    );
  }

  const youtubeId = getYouTubeId(post.youtubeUrl);

  // Strip HTML tags from content for the meta description
  const plainTextContent = post.content?.replace(/<[^>]+>/g, '') || '';
  const metaDescription = plainTextContent.slice(0, 155).trim();

  return (
    <Layout featureType="blog">
      <Helmet>
        <title>{`${post.title} – Crich Baptist Church`}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://crichbaptistchurch.org/blog/${id}`} />
        {post.coverImage && <meta property="og:image" content={post.coverImage} />}
        <link rel="canonical" href={`https://crichbaptistchurch.org/blog/${id}`} />
      </Helmet>
      <div className="blog-post">

        <Link to="/blog">
          <img src={arrow_icon} alt="Back to Blog" className="back-to-blog" />
        </Link>

        {/* Cover image */}
        {post.coverImage && (
          <div className="post-cover">
            <img src={post.coverImage} alt={post.title} />
          </div>
        )}

        {/* Title & meta */}
        <h1>{post.title}</h1>
        <p className="post-meta">
          {post.authorName && <span className="post-author">By {post.authorName}</span>}
          {post.authorName && <span className="meta-sep">·</span>}
          <span>Published: {formatDate(post.createdAt)}</span>
          {post.category && <><span className="meta-sep">·</span><span className="category-badge">{post.category}</span></>}
        </p>

        {/* Body content */}
        <div
          className="post-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
        />

        {/* YouTube embed */}
        {youtubeId && (
          <div className="post-section">
            <div className="youtube-embed">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}`}
                title="Embedded video"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Photo gallery */}
        {post.galleryImages?.length > 0 && (
          <div className="post-section">
            <h2 className="gallery-heading">Gallery</h2>
            <div className="post-gallery">
              {post.galleryImages.map((img, i) => (
                <figure key={i} className="gallery-item" onClick={() => setLightboxImg(img)}>
                  <img src={img.url} alt={img.caption || `Gallery image ${i + 1}`} />
                  {img.caption && <figcaption>{img.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div className="lightbox" onClick={() => setLightboxImg(null)}>
          <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImg(null)}>✕</button>
            <img src={lightboxImg.url} alt={lightboxImg.caption || ''} />
            {lightboxImg.caption && <p className="lightbox-caption">{lightboxImg.caption}</p>}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BlogPost;